import React from 'react';
import { Layout } from '@apps/card/components/Layout';
import { useCustomizationContext } from '../../../CardCustomizer/useCardCustomization';
import { SidePanelPriceBreakdownBlock } from '../../../CardCustomizer/components/SidePanelPriceBreakdownBlock';
import { useCardCustomizerTelemetry } from '../../../CardCustomizer/CardCustomizer.telemetry';
import { useEditingMode } from '../../../CardCustomizer/useEditingMode';
import { CardFrontConfigPanel } from '../../../CardCustomizer/steps/CardDesign/CardFrontConfigPanel';
import { usePopulateEventLink } from '@apps/card/routes/CardCustomizer/steps/CardDesign/usePopulateEventLink';
import { QuantityChoiceGroup } from '@apps/card/routes/CardCustomizer/steps/CardDesign/QuantityChoiceGroup';
import { AttributesForStep } from '@apps/card/components/ProductUIConfig/components/AttributesForStep';
import { getKeys } from '@shared/utils/object';
import { useNavigationContext, printNavigationStepToLabel } from '@apps/card/routes/CardCustomizer/useNavigationState';
import { Flex, TextV2 } from '@withjoy/joykit';
import { ReadOnlySizeChoiceGroup } from '@apps/card/routes/Dashboard/components/ProductDetailsPage/components/SizeChoiceGroup';
import { useTranslation } from '@shared/core';
import { ProductChoiceGroup } from '@apps/card/routes/CardCustomizer/components/ProductChoiceGroup/ProductChoiceGroup';

const CardFrontSubtotal = (props: { subtotal?: string }) => {
  const { subtotal } = props;
  const { tq } = useTranslation('stationery');

  if (!subtotal) {
    return null;
  }

  return (
    <Flex columnGap={4}>
      <TextV2 typographyVariant="button1">{tq`Subtotal`}</TextV2>
      <TextV2
        typographyVariant="button1"
        color="mono12"
      >
        {subtotal}
      </TextV2>
    </Flex>
  );
};

export const CardFrontCustomizations = (props: { isInsidePDP?: boolean; subtotal?: string }) => {
  const { mode } = useEditingMode();
  const [_, updateCardFront] = useCustomizationContext(draft => draft.config.card.front, mode === 'order');
  const { subtotal } = props;

  usePopulateEventLink(cb => updateCardFront(cb));

  return (
    <>
      <AttributesForStep
        isPDP={props.isInsidePDP}
        step="cardFront"
      />
      <QuantityChoiceGroup />
      <CardFrontSubtotal subtotal={subtotal} />
    </>
  );
};

/**
 * The standalone-envelope product's PDP block — the two pieces of `CardFrontCustomizations` that
 * are not about a card front. It is a sibling rather than a flag on that component because the
 * difference is structural: an envelope draft carries no `card.front` (§2.1.1 of
 * doc/standalone-envelopes/PLAN.md), so neither the `cardFront` attribute group nor the
 * `draft.config.card.front` read above has anything to act on.
 */
export const EnvelopeCustomizations = ({ subtotal }: { subtotal?: string }) => {
  return (
    <>
      <QuantityChoiceGroup />
      <CardFrontSubtotal subtotal={subtotal} />
    </>
  );
};

export const PaperCardFrontConfigPanel = ({ hideStickyCtaBar }: { hideStickyCtaBar: boolean }) => {
  const { navigateForward, currentStep, steps, draftId } = useNavigationContext();
  const { customizationButtonInteracted } = useCardCustomizerTelemetry();

  const stepNames = getKeys(steps);
  const nextStepName = stepNames[stepNames.findIndex(name => name === currentStep) + 1];

  return (
    <CardFrontConfigPanel
      hideStickyCtaBar={hideStickyCtaBar}
      onNext={() => {
        navigateForward();
        customizationButtonInteracted({ property: 'stickyCta_nextButton' });
      }}
      nextStepLabel={printNavigationStepToLabel[nextStepName]}
      renderCustomizations={() => (
        <>
          <Layout.SidePanelBlock gap={7}>
            <ProductChoiceGroup draftId={draftId} />
            <ReadOnlySizeChoiceGroup />
            <CardFrontCustomizations />
          </Layout.SidePanelBlock>
          <SidePanelPriceBreakdownBlock />
        </>
      )}
    />
  );
};
